import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, ArrowRight, Edit2, Phone, Trash2, Truck, CalendarClock, MessageCircle } from "lucide-react";
import { STATUS_META, STATUS_ORDER, fmtR, fmtDate, fmtDateTime } from "./orderStatus";

export const OrderTicketCard = ({ order, onEdit, onDelete, onStatus, onNotify }) => {
  const meta = STATUS_META[order.status] || STATUS_META.open;
  const tid = order.ticket_no;
  return (
    <Card className={`transition-shadow hover:shadow-md ${order.is_overdue ? "border-red-400 bg-red-50/40" : ""}`} data-testid={`order-card-${tid}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-gray-500" data-testid={`order-ticket-no-${tid}`}>{tid}</span>
              <Badge variant="outline" className={meta.badge} data-testid={`order-status-${tid}`}>{meta.label}</Badge>
              {order.is_overdue && <Badge className="bg-red-600 text-white" data-testid={`order-overdue-${tid}`}><AlertTriangle className="h-3 w-3 mr-1" />Overdue</Badge>}
            </div>
            <div className="mt-1 text-lg font-semibold" data-testid={`order-customer-${tid}`}>{order.customer_name}</div>
            {order.customer_phone && <div className="flex items-center gap-1 text-sm text-gray-600"><Phone className="h-3 w-3" />{order.customer_phone}</div>}
          </div>
          <div className="text-right text-sm">
            <div className="text-lg font-bold" data-testid={`order-total-${tid}`}>{fmtR(order.total)}</div>
            {order.deposit_paid > 0 && <div className="text-gray-600">Deposit {fmtR(order.deposit_paid)}</div>}
            <div className="font-semibold text-orange-600" data-testid={`order-balance-${tid}`}>Due {fmtR(order.balance_due)}</div>
          </div>
        </div>

        <ul className="divide-y rounded-md border text-sm">
          {order.items.map((it, i) => (
            <li key={i} className="flex justify-between gap-2 px-3 py-1.5">
              <span><span className="font-semibold">{it.qty} ×</span> {it.description}</span>
              <span className="whitespace-nowrap text-gray-600">{fmtR(it.price)} = {fmtR(it.line_total)}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
          {order.supplier_name && <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{order.supplier_name}</span>}
          {order.expected_date && <span className={`flex items-center gap-1 ${order.is_overdue ? "font-semibold text-red-600" : ""}`}><CalendarClock className="h-3 w-3" />Expected {fmtDate(order.expected_date)}</span>}
          <span>Opened {fmtDate(order.created_at)}</span>
        </div>
        {(order.notifications || []).length > 0 && (
          <div className="space-y-0.5" data-testid={`order-notifications-${tid}`}>
            {order.notifications.map((n, i) => (
              <div key={i} className="flex items-center gap-1 text-xs text-green-700"><MessageCircle className="h-3 w-3" />Notified via WhatsApp on {fmtDateTime(n.at)}{n.by ? ` by ${n.by}` : ""}</div>
            ))}
          </div>
        )}
        {order.notes && <p className="whitespace-pre-wrap rounded bg-yellow-50 px-3 py-2 text-sm" data-testid={`order-notes-${tid}`}>{order.notes}</p>}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => onEdit(order)} data-testid={`order-edit-btn-${tid}`}><Edit2 className="h-4 w-4 mr-1" />Edit</Button>
            {order.status === "arrived" && (
              <Button size="sm" variant="ghost" className="text-green-700" onClick={() => onNotify(order)} data-testid={`order-notify-btn-${tid}`}><MessageCircle className="h-4 w-4 mr-1" />Notify</Button>
            )}
            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(order)} data-testid={`order-delete-btn-${tid}`}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
          </div>
          <div className="flex items-center gap-2">
            <Select value={order.status} onValueChange={(v) => onStatus(order, v)}>
              <SelectTrigger className="h-8 w-[190px] text-xs" data-testid={`order-status-select-${tid}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
              </SelectContent>
            </Select>
            {meta.next && (
              <Button size="sm" onClick={() => onStatus(order, meta.next)} data-testid={`order-advance-btn-${tid}`}>
                {meta.nextLabel}<ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
